import { HorizonError } from './horizon-error'
import { withRetryOn429 } from './retry-on-429'

/**
 * How a request path combines with `baseUrl`. `resolve` treats the path as
 * absolute on the base's origin, dropping any path the base carries; `append`
 * keeps the base's path, for a base that is a path-prefixed relay.
 */
export type BaseUrlJoin = 'resolve' | 'append'

const TRAILING_SLASHES = /\/+$/
const LEADING_SLASHES = /^\/+/

export interface HorizonConfig {
  baseUrl: string
  /** Defaults to `resolve`. */
  baseUrlJoin?: BaseUrlJoin
  sormBaseUrl?: string
  prepareHeaders?: (headers: Headers) => void
  fetchOptions?: Omit<RequestInit, 'body' | 'method' | 'headers'>
}

let config: HorizonConfig | null = null

export const configureHorizon = (cfg: HorizonConfig) => {
  config = cfg
}

const getBody = <T>(c: Response | Request): Promise<T> => {
  const contentType = c.headers.get('content-type')

  if (contentType?.includes('application/json')) {
    return c.json()
  }

  // Payloads orval types as `Blob` (their schema declares `format: binary`):
  // PDF documents and CSV exports. The caller converts the Blob as needed
  // (e.g. `await data.text()` for CSV).
  if (
    contentType?.includes('application/pdf') ||
    contentType?.includes('text/csv')
  ) {
    return c.blob() as Promise<T>
  }

  return c.text() as Promise<T>
}

const getUrl = ({
  baseUrl,
  baseUrlJoin = 'resolve',
  contextUrl,
}: {
  baseUrl: string
  baseUrlJoin?: BaseUrlJoin
  contextUrl: string
}): string => {
  switch (baseUrlJoin) {
    case 'resolve':
      return new URL(contextUrl, baseUrl).toString()
    case 'append':
      return `${baseUrl.replace(TRAILING_SLASHES, '')}/${contextUrl.replace(LEADING_SLASHES, '')}`
    default:
      return baseUrlJoin satisfies never
  }
}

export type ErrorType<_Error> = HorizonError

// Reference: https://github.com/orval-labs/orval/blob/master/samples/next-app-with-fetch/custom-fetch.ts
// biome-ignore lint/suspicious/useAwait: async required by Orval custom-fetch contract
export const customFetch = async <T>(
  url: string,
  options: RequestInit,
): Promise<T> => {
  if (!config) {
    throw new Error(
      'Horizon client not configured. Call configureHorizon() first.',
    )
  }
  const isSormRequest = url.includes('/sorm/')
  const requestUrl =
    isSormRequest && config.sormBaseUrl
      ? `${config.sormBaseUrl}${url}`
      : getUrl({
          baseUrl: config.baseUrl,
          baseUrlJoin: config.baseUrlJoin,
          contextUrl: url,
        })
  const requestHeaders = new Headers(options.headers)
  config.prepareHeaders?.(requestHeaders)

  const requestInit: RequestInit = {
    ...config.fetchOptions,
    ...options,
    headers: requestHeaders,
  }

  return withRetryOn429(async () => {
    const response = await fetch(requestUrl, requestInit)

    // NDJSON (streaming) responses are typed by orval as
    // `{ stream: TypedResponse<…> }`. Hand back the raw Response so the caller
    // reads the stream itself, rather than customFetch buffering it into `data`.
    // Non-2xx responses fall through to the normal parse-and-throw path.
    const contentType = response.headers.get('content-type')
    if (response.ok && contentType?.includes('application/x-ndjson')) {
      return {
        stream: response,
        status: response.status,
        headers: response.headers,
      } as T
    }

    const data = await getBody<T>(response)

    if (!response.ok) {
      throw new HorizonError(response.status, data, response.headers)
    }

    return { status: response.status, data, headers: response.headers } as T
  }, requestInit.signal)
}
